<script setup lang="ts">
  import { computed } from 'vue'
  import { Button } from '@/components/ui/button'
  import { useThemeColor } from '@/composables/useThemeColorV3'
  import { useTranslation } from '@/composables/useTranslation'
  import { useI18n } from 'vue-i18n'
  import { entitiesDE, entitiesEN } from '@/i18n/entity'

  const { t } = useTranslation({
    en: {
      addAnotherTitle: 'Would you like to add another {participantType}?',
      addAnotherDescription:
        'You have completed all questions for this {participantType}. You can add another one or continue to the next section.',
      yesAddAnother: 'Yes, add another',
      noContinue: 'No, continue'
    },
    de: {
      addAnotherTitle: 'Möchten Sie einen weiteren {participantType} hinzufügen?',
      addAnotherDescription:
        'Sie haben alle Fragen für diesen {participantType} beantwortet. Sie können einen weiteren hinzufügen oder zum nächsten Abschnitt fortfahren.',
      yesAddAnother: 'Ja, weiteren hinzufügen',
      noContinue: 'Nein, weiter'
    }
  })

  const props = defineProps<{
    participantType: string
  }>()

  const emit = defineEmits<{
    (e: 'add-another'): void
    (e: 'continue'): void
  }>()

  const { locale } = useI18n()
  const themeColor = computed(() => useThemeColor().value)

  const participantTypeName = computed(() => {
    const entityTranslations = locale.value === 'de' ? entitiesDE : entitiesEN
    return (
      entityTranslations[props.participantType as keyof typeof entityTranslations] ||
      props.participantType
    )
  })

  const handleAddAnother = () => {
    emit('add-another')
  }

  const handleContinue = () => {
    emit('continue')
  }
</script>

<template>
  <div
    class="flex min-h-[360px] flex-col justify-between rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
  >
    <div class="flex flex-col gap-4">
      <h2 class="text-lg font-medium leading-tight text-gray-900">
        {{
          t('addAnotherTitle', {
            participantType: participantTypeName
          })
        }}
      </h2>

      <p class="text-sm font-normal leading-tight text-gray-600">
        {{
          t('addAnotherDescription', {
            participantType: participantTypeName
          })
        }}
      </p>
    </div>

    <div class="flex justify-end gap-2">
      <Button
        variant="secondary"
        class="flex h-8 items-center justify-center gap-1.5 rounded-md px-4 text-sm font-medium shadow-sm"
        data-cy="RepeatSectionPromptContinueButton"
        @click="handleContinue"
      >
        {{ t('noContinue') }}
      </Button>
      <Button
        :class="[
          `flex h-8 items-center justify-center gap-1.5 rounded-md px-4 text-sm font-medium text-white
          shadow-sm`,
          `bg-${themeColor}-600 hover:bg-${themeColor}-700 focus-visible:ring-${themeColor}-600`
        ]"
        data-cy="RepeatSectionPromptAddAnotherButton"
        @click="handleAddAnother"
      >
        {{ t('yesAddAnother') }}
      </Button>
    </div>
  </div>
</template>
